import { computed, onScopeDispose, watch } from "vue";
import { isNavigationFailure, useRoute, useRouter } from "vue-router";
import { UserStoreFactory } from "@/stores/user";
import { fetchApiKey } from "@/stores/util.dom";
import {
  languageSwitchState,
  beginLanguageOperation,
  registerLanguagePage,
  type PageLanguageLoader,
} from "@/utils/pageLanguageNavigation";

const initializedLanguages = new WeakMap<
  object,
  { owner: string; lang: string }
>();

/** Keeps native page routes aligned with the legacy `{{_query.lang}}` contract. */
export function usePageLanguage() {
  const route = useRoute();
  const router = useRouter();
  const useUserStore = UserStoreFactory(fetchApiKey() + "_user");
  let active = true;
  let releaseRetry: (() => void) | undefined;
  onScopeDispose(() => {
    active = false;
    releaseRetry?.();
  });

  const user = useUserStore();
  const owner = () =>
    JSON.stringify([user.merchantId, user.customerId || user.token]);
  const isInitialized = (lang: string) => {
    const initialized = initializedLanguages.get(user);
    return initialized?.owner === owner() && initialized.lang === lang;
  };
  const commitLanguage = (lang: string) => {
    user.updateSelectedCurrentLang(lang);
    localStorage.setItem("currentLang", lang);
    initializedLanguages.set(user, { owner: owner(), lang });
  };

  const currentLang = computed(() => {
    const value = route.query.lang;
    const queryLang = Array.isArray(value) ? value[0] : value;
    return (
      (typeof queryLang === "string" && queryLang) ||
      useUserStore().currentSelectedLang ||
      useUserStore().defaultLang ||
      "en"
    );
  });

  const initLang = async () => {
    const lang = currentLang.value;
    const initialOwner = owner();
    const isCurrent = () =>
      active && currentLang.value === lang && initialOwner === owner();
    try {
      await user.getCurrentLang(lang, isCurrent);
      if (isCurrent()) commitLanguage(lang);
    } catch {
      // Dictionary failure must not skip guest/category initialization. The
      // selector can retry even when the requested locale has not changed.
      if (isCurrent()) languageSwitchState.failed = true;
    }
    return lang;
  };

  const changeLanguage = async (lang: string) => {
    if (languageSwitchState.pending || languageSwitchState.initializing)
      return false;
    if (lang === currentLang.value) {
      if (isInitialized(lang)) {
        languageSwitchState.failed = false;
        return true;
      }
      const release = beginLanguageOperation();
      releaseRetry = release;
      languageSwitchState.failed = false;
      try {
        await initLang();
        return active && isInitialized(lang);
      } catch {
        if (active) languageSwitchState.failed = true;
        return false;
      } finally {
        release();
        if (releaseRetry === release) releaseRetry = undefined;
      }
    }
    return !isNavigationFailure(
      await router.replace({
        name: route.name || undefined,
        params: route.params,
        query: { ...route.query, lang },
        hash: route.hash,
      }),
    );
  };

  const onLanguageChange = (
    prepare: PageLanguageLoader,
    isReady: () => boolean = () => true,
  ) => {
    const user = useUserStore();
    onScopeDispose(
      registerLanguagePage({
        prepare,
        load: (lang, isCurrent, signal) =>
          user.prepareCurrentLang(lang, isCurrent, signal),
        ready: isReady,
        owner: () =>
          JSON.stringify([user.merchantId, user.customerId || user.token]),
        supports: (lang) =>
          (user.homepageConfig?.widgetLanguages || []).includes(lang),
        commit: commitLanguage,
      }),
    );
    watch(
      isReady,
      (ready) => {
        languageSwitchState.initializing = !ready;
      },
      { immediate: true },
    );
  };

  watch(
    currentLang,
    (lang) => {
      document.documentElement.setAttribute(
        "dir",
        lang.toLowerCase().startsWith("ar") ? "rtl" : "ltr",
      );
      document.documentElement.lang = lang;
    },
    { immediate: true },
  );

  return {
    currentLang,
    initLang,
    changeLanguage,
    onLanguageChange,
    languageSwitchState,
  };
}
